import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import { Formik } from 'formik';
import { get } from 'lodash';
import { getModalData, getCreateServiceRequest, createLoadingSelector } from '../../../selectors';
import { hideModal } from '../../../actions/modal.actions';
import {
    getAccountTypes,
    getAccountTypeDetails,
    verifyAuthCode,
    sendAuthCode,
    createNewAccount,
    createNewOrder
} from '../../../actions/createServiceRequest.actions';
import { billingAddressValidation } from '../../../actions/createAccount.actions';
import isEmpty from '../../../utils/helpers';
import axiosInstance from '../../../utils/axios-instance';
import { toastr } from 'react-redux-toastr';
import { generateFullZip } from 'src/containers/CreateAccount/utils';
import AccountHolderLoader from 'src/components/Loader/AccountHolderLoader';

class AccountModal extends Component {
    constructor(props, context) {
        super(props, context);

        let initialStatus = 'PROSPECT';

        if (props.modalProps.validatedAddress) {
            switch (props.modalProps.validatedAddress.allowedServiceOrderType) {
                case 'NEW_SURVEY':
                    initialStatus = 'SURVEY';
                    break;
                case 'NEW_SIGNUP':
                    initialStatus = 'SIGNUP';
                    break;
                case 'NEW_SERVICE':
                    initialStatus = 'ACTIVE';
                    break;
                default:
                    break;
            }
        }

        this.state = {
            initialValues: {
                accountHolder: {
                    accountType: '',
                    firstName: '',
                    middleName: '',
                    lastName: '',
                    mobileNumber: {
                        number: '',
                        verified: false
                    },
                    homeNumber: {
                        number: '',
                        verified: false
                    },
                    businessNumber: {
                        number: '',
                        verified: false
                    },
                    personalEmail: '',
                    businessEmail: '',
                    company: '',
                    ssn: '',
                    taxId: '',
                    driverLicense: '',
                    dateOfBirth: null,
                    memberNumber: ''
                },
                accountTermsInfo: {
                    accountTerm: '',
                    accountTier: '',
                    billCycle: ''
                },
                billingDetails: {
                    showBillingCycle: false
                },
                initialStatus: initialStatus,
                locationId: this.props.currentLocation.id,
                address: this.props.modalProps.validatedAddress.formattedAddress
            },

            // auth code state
            authCode: '',
            showVerificationMobileNumber: false,
            isAuthCodeValid: null,

            // address validation state
            addressValidationLoader: false,
            showSuggestedAddress: true,
            useSuggestedAddress: false,
            suggestedAddress: undefined,
            suggestedAddressError: undefined,

            // dynamic accountType fields state
            accountHolderEnableFields: {},
            accountHolderRequiredFields: {},
            accountHolderOptionalFields: {},
            accountTypeDetailsLoaded: false
        };
    }

    componentDidMount() {
        this.props.getAccountTypes();
        this.handleValidateBillingAddress();
    }

    /* =========================================================================
     * Dynamic Yup schema based on backend-required fields
     * ========================================================================= */
    getValidationSchema = () => {
        const { accountHolderEnableFields, accountHolderRequiredFields } = this.state;

        const isRequired = (name) =>
            Boolean(accountHolderEnableFields[name]) && Boolean(accountHolderRequiredFields[name]);

        const requiredString = () => Yup.string().min(1, 'Required').required('Required');
        const optionalString = () => Yup.string().nullable();

        const requiredEmail = () => Yup.string().email('Invalid email format').required('Required');
        const optionalEmail = () => Yup.string().email('Invalid email format').nullable();

        const maybeString = (name) => (isRequired(name) ? requiredString() : optionalString());
        const maybeEmail = (name) => (isRequired(name) ? requiredEmail() : optionalEmail());

        const maybePhoneObj = (name) =>
            Yup.object().shape({
                number: isRequired(name) ? requiredString() : optionalString(),
                verified: Yup.boolean()
            });

        return Yup.object().shape({
            accountHolder: Yup.object().shape({
                accountType: Yup.string().min(1, 'Required').required('Required'),

                firstName: isRequired('firstName')
                    ? Yup.string().required('Required')
                    : Yup.string().nullable(),

                middleName: maybeString('middleName'),

                lastName: isRequired('lastName')
                    ? Yup.string().required('Required')
                    : Yup.string().nullable(),

                company: maybeString('company'),

                personalEmail: maybeEmail('personalEmail'),
                businessEmail: maybeEmail('businessEmail'),

                mobileNumber: maybePhoneObj('mobileNumber'),
                homeNumber: maybePhoneObj('homeNumber'),
                businessNumber: maybePhoneObj('businessNumber'),

                ssn: maybeString('ssn'),
                taxId: maybeString('taxId'),
                driverLicense: maybeString('driverLicense'),

                // Date field: required/optional
                dateOfBirth: isRequired('dateOfBirth')
                    ? Yup.date().typeError('Required').required('Required')
                    : Yup.date().nullable(),

                memberNumber: maybeString('memberNumber')
            }),

            accountTermsInfo: Yup.object().shape({
                accountTerm: Yup.string().nullable()
            })
        });
    };

    /* =========================================================================
     * Address validation helpers
     * ========================================================================= */
    closeAddressSuggestion = () => {
        this.setState({
            addressValidationLoader: false,
            showSuggestedAddress: false
        });
    };

    useSuggestedAddress = () => {
        this.setState({ showSuggestedAddress: false, useSuggestedAddress: true });
    };

    handleValidateBillingAddress = () => {
        const { validatedAddress } = this.props.modalProps;
        const addressRequest = {
            addressWrapper: {
                address1: validatedAddress.address1,
                address2: validatedAddress.address2,
                address3: validatedAddress.address3,
                city: validatedAddress.city,
                state: validatedAddress.state.abbreviation,
                zipCode: validatedAddress.zipCode
            }
        };

        this.setState({ addressValidationLoader: true });

        this.props.billingAddressValidation(addressRequest).then((response) => {
            if (response.data.status === 'OK') {
                this.setState({
                    addressValidationLoader: false,
                    suggestedAddress: response.data,
                    suggestedAddressError: undefined,
                    showSuggestedAddress: true
                });
            } else if (response.data.status === 'ERROR') {
                this.setState({
                    addressValidationLoader: false,
                    suggestedAddress: undefined,
                    suggestedAddressError: response.data.errorDescription,
                    showSuggestedAddress: true
                });
            } else {
                this.setState({
                    addressValidationLoader: false
                });
            }
        });
    };

    formatAddress = (validatedAddress) => {
        const { address1, address2, city, state, zipCode, country } = validatedAddress;
        const addressParts = [];
        if (address1) addressParts.push(address1);
        if (address2) addressParts.push(address2);
        if (city) addressParts.push(city);
        if (state && state.abbreviation) addressParts.push(state.abbreviation);
        if (zipCode) addressParts.push(zipCode);
        if (country && country.name) addressParts.push(country.name);
        const formattedAddress = addressParts.join(', ');

        return <address>{formattedAddress}</address>;
    };

    /* =========================================================================
     * Dynamic AccountType handler (build enabled/required/optional field maps)
     * ========================================================================= */
    accountTypeHandler = async (accountTypeId, formik) => {
        if (!accountTypeId) return;

        const { setFieldValue, resetForm, setTouched } = formik;

        // reset to base initial values, clear touched
        resetForm({ values: this.state.initialValues });
        setTouched({});

        setFieldValue('accountHolder.accountType', accountTypeId);

        // Clear dynamic flags while loading
        this.setState({
            accountHolderEnableFields: {},
            accountHolderRequiredFields: {},
            accountHolderOptionalFields: {},
            accountTypeDetailsLoaded: false
        });

        const response = await this.props.getAccountTypeDetails(accountTypeId);
        if (!response) return;

        const fields = response.fields || [];
        const enableFieldsState = {};

        fields.forEach((element) => {
            enableFieldsState[element.name] = true;

            // mobile/home/business numbers are nested objects in this UI
            if (['mobileNumber', 'homeNumber', 'businessNumber'].includes(element.name)) {
                setFieldValue(`accountHolder.${element.name}`, { number: '', verified: false });
            } else {
                setFieldValue(`accountHolder.${element.name}`, '');
            }
        });

        const requiredFields = fields
            .filter((x) => x.required === true)
            .reduce((acc, element) => {
                acc[element.name] = true;
                return acc;
            }, {});

        const optionalFields = fields
            .filter((x) => x.required !== true)
            .reduce((acc, element) => {
                acc[element.name] = true;
                return acc;
            }, {});

        // Billing cycle behavior
        if (response.allowedBillCycles) {
            const billCycles = response.allowedBillCycles.split(',');
            const defaultBillCycle = Number(
                billCycles
                    .find((x) => x.includes('*'))
                    .replace('*', '')
            );

            setFieldValue('accountTermsInfo.billCycle', defaultBillCycle);
            setFieldValue('billingDetails.showBillingCycle', response.enableServiceBill === true);
        }

        this.setState({
            accountHolderEnableFields: enableFieldsState,
            accountHolderRequiredFields: requiredFields,
            accountHolderOptionalFields: optionalFields,
            accountTypeDetailsLoaded: true
        });
    };

    /* =========================================================================
     * Auth Code helpers
     * ========================================================================= */
    resetAuthCodeState = (setFieldValue) => {
        this.setState({
            showVerificationMobileNumber: false,
            isAuthCodeValid: null,
            authCode: ''
        });
        setFieldValue('accountHolder.mobileNumber.verified', false);
    };

    onSubmitAuthCode = (number) => {
        const data = {
            phoneNumber: number,
            requester: 'alex'
        };

        this.props.sendAuthCode(data).then(() => {
            this.setState({
                showVerificationMobileNumber: true,
                isAuthCodeValid: null,
                authCode: ''
            });
        });
    };

    onChangeAuthCodeHandler = (e) => {
        this.setState({
            authCode: e.target.value
        });
    };

    onSubmitVerifyAuthCode = (number, setFieldValue) => {
        const data = {
            authcode: this.state.authCode,
            phoneNumber: number,
            requester: 'alex'
        };

        this.props.verifyAuthCode(data).then((response) => {
            if (response.data.code === 200) {
                this.setState({ isAuthCodeValid: true });
                setFieldValue('accountHolder.mobileNumber.verified', true);
            }
            if (response.data.error) {
                this.setState({ isAuthCodeValid: false });
                setFieldValue('accountHolder.mobileNumber.verified', false);
            }
        });
    };

    /* =========================================================================
     * Email validation (async)
     * ========================================================================= */
    checkEmail = (status, setStatus, field, value) => {
        const resetStatus = !isEmpty(status) ? { ...status } : {};

        delete resetStatus[field];
        setStatus(resetStatus);

        axiosInstance
            .get('/verification/email/valid', { params: { email: value } })
            .catch((error) => {
                const newStatus = {
                    [field]: get(error, 'response.data.error.message', 'Invalid email'),
                    ...resetStatus
                };
                setStatus(newStatus);
            });
    };

    /* =========================================================================
     * Submit
     * ========================================================================= */
    formSubmit = (values, actions) => {
        // remove any non-submit props (FIX: use && not ||)
        const data = Object.keys(values).reduce((object, key) => {
            if (key !== 'validatedAddressWrapper' && key !== 'validatedAddress') {
                object[key] = values[key];
            }
            return object;
        }, {});

        const state = this.props.modalProps.validatedAddressWrapper.state;

        // Always keep service address for prospect (existing behavior)
        if (this.state.initialValues.initialStatus === 'PROSPECT') {
            data.accountHolder.serviceAddress = this.props.modalProps.validatedAddress.addressSearchFormated;
        }

        // billingDetails payload
        data.billingDetails = data.billingDetails || {};
        if (this.state.useSuggestedAddress) {
            data.billingDetails.billingAddress = this.state.suggestedAddress;
            data.billingDetails.billingAddress.state = this.state.suggestedAddress.state;
            data.billingDetails.billingAddress.verified = true;
        } else {
            data.billingDetails.billingAddress = this.props.modalProps.validatedAddress;
            data.billingDetails.billingAddress.state = this.props.modalProps.validatedAddress.state;
            data.billingDetails.billingAddress.verified = false;
            data.billingDetails.billingAddress.zipCode = this.props.modalProps.validatedAddress.zipCode;
            data.billingDetails.billingAddress.zip4 = this.props.modalProps.validatedAddress.zip4;
        }

        data.accountHolder.serviceAddress = this.props.modalProps.validatedAddress.addressSearchFormated;

        if (this.props.modalProps.validatedAddress.accountCreationLocationIdOverride) {
            data.accountCreationLocationIdOverride =
                this.props.modalProps.validatedAddress.accountCreationLocationIdOverride;
        }

        // OPTIONAL: only submit enabled accountHolder fields (prevents stale values)
        const { accountHolderEnableFields } = this.state;
        const filteredAccountHolder = Object.entries(values.accountHolder || {}).reduce((acc, [key, val]) => {
            if (key === 'accountType') {
                acc[key] = val;
                return acc;
            }
            if (accountHolderEnableFields[key]) {
                acc[key] = val;
            }
            return acc;
        }, {});
        data.accountHolder = {
            ...data.accountHolder,
            ...filteredAccountHolder
        };

        this.props.createNewAccount(data).then((response) => {
            if (isEmpty(this.props.modalProps.validatedAddress.allowedServiceOrderType)) {
                actions.setSubmitting(false);
                return;
            }

            if (response.status === 201 || response.status === 200) {
                const quotesData = { ...this.props.modalProps };
                quotesData.accountId = response.data.id;
                quotesData.address = this.props.modalProps.validatedAddress.addressSearchFormated;
                quotesData.validatedAddress = true;
                quotesData.type = this.props.modalProps.validatedAddress.allowedServiceOrderType;
                quotesData.validatedAddressWrapper.state = state;

                this.props.createNewOrder(quotesData);
            } else {
                actions.setSubmitting(false);

                let errorMessage = 'Something went wrong';
                if (
                    response &&
                    response.response &&
                    response.response.data &&
                    response.response.data.error &&
                    response.response.data.error.message
                ) {
                    errorMessage = response.response.data.error.message;
                }
                toastr.error(errorMessage, { timeOut: 2000, position: 'top-center' });
            }
        });
    };

    /* =========================================================================
     * Title
     * ========================================================================= */
    getTitleText = () => {
        let titleText = 'Save Prospect';

        if (this.props.modalProps.validatedAddress) {
            switch (this.props.modalProps.validatedAddress.allowedServiceOrderType) {
                case 'NEW_SURVEY':
                    titleText = 'Save Survey';
                    break;
                case 'NEW_SIGNUP':
                    titleText = 'Save Signup';
                    break;
                case 'NEW_SERVICE':
                    titleText = 'Save Service';
                    break;
                default:
                    break;
            }
        }

        return <h5 className="modal-title">{titleText}</h5>;
    };

    render() {
        const {
            initialValues,
            authCode,
            isAuthCodeValid,
            showVerificationMobileNumber,
            addressValidationLoader,
            suggestedAddress,
            showSuggestedAddress,
            suggestedAddressError,
            accountHolderEnableFields,
            accountHolderOptionalFields,
            accountTypeDetailsLoaded
        } = this.state;

        const { accountTypesData, accountTermsData, authCodeLoader, authCodeVerifyLoader, hideModal } =
            this.props;

        const hasSelectedAccountType = Boolean(initialValues.accountHolder.accountType);

        return (
            <Fragment>
                <div className="modal show" tabIndex="-1" style={{ display: 'block' }}>
                    <Formik
                        initialValues={initialValues}
                        enableReinitialize
                        validationSchema={this.getValidationSchema()}
                        onSubmit={this.formSubmit}
                        render={({
                                     handleChange,
                                     handleSubmit,
                                     handleBlur,
                                     values,
                                     errors,
                                     touched,
                                     isSubmitting,
                                     setFieldValue,
                                     resetForm,
                                     setTouched,
                                     status,
                                     setStatus
                                 }) => (
                            <form onSubmit={handleSubmit}>
                                <div className="modal-dialog modal-lg" role="document">
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            {this.getTitleText()}
                                            <button type="button" className="close" onClick={hideModal}>
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                        </div>

                                        <div className="modal-body cmv-form">
                                            {/* Account Type + Account Term */}
                                            <div className="form-row">
                                                <div className="form-group col-md-6">
                                                    <label>Account Type</label>
                                                    <select
                                                        name="accountHolder.accountType"
                                                        className={
                                                            get(errors, 'accountHolder.accountType') && get(touched, 'accountHolder.accountType')
                                                                ? 'form-control is-invalid'
                                                                : 'form-control'
                                                        }
                                                        value={values.accountHolder.accountType}
                                                        onChange={(e) =>
                                                            this.accountTypeHandler(e.target.value, {
                                                                setFieldValue,
                                                                resetForm,
                                                                setTouched
                                                            })
                                                        }
                                                        onBlur={handleBlur}
                                                    >
                                                        <option value="" disabled>
                                                            Select
                                                        </option>
                                                        {accountTypesData.map((type) => (
                                                            <option value={type.id} key={type.id}>
                                                                {type.description}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {get(errors, 'accountHolder.accountType') && get(touched, 'accountHolder.accountType') && (
                                                        <div className="invalid-feedback">{get(errors, 'accountHolder.accountType')}</div>
                                                    )}
                                                </div>

                                                <div className="form-group col-md-6">
                                                    <label>Account Term</label>
                                                    <select
                                                        name="accountTermsInfo.accountTerm"
                                                        className={
                                                            get(errors, 'accountTermsInfo.accountTerm') &&
                                                            get(touched, 'accountTermsInfo.accountTerm')
                                                                ? 'form-control is-invalid'
                                                                : 'form-control'
                                                        }
                                                        value={values.accountTermsInfo.accountTerm}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                    >
                                                        <option value="">Select</option>
                                                        {accountTermsData.map((type) => (
                                                            <option value={type.id} key={type.id}>
                                                                {type.description}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {get(errors, 'accountTermsInfo.accountTerm') &&
                                                        get(touched, 'accountTermsInfo.accountTerm') && (
                                                            <div className="invalid-feedback">{get(errors, 'accountTermsInfo.accountTerm')}</div>
                                                        )}
                                                </div>
                                            </div>

                                            {values.accountHolder.accountType && !accountTypeDetailsLoaded ? (
                                                <AccountHolderLoader />
                                            ) : (
                                                <>
                                                    {accountHolderEnableFields.company && (
                                                        <div className="form-row">
                                                            <div className="form-group col-md-6">
                                                                <label htmlFor="company">Company</label>
                                                                <input
                                                                    className={
                                                                        get(errors, 'accountHolder.company') && get(touched, 'accountHolder.company')
                                                                            ? 'form-control is-invalid'
                                                                            : 'form-control'
                                                                    }
                                                                    name="accountHolder.company"
                                                                    placeholder={
                                                                        'Company' + (accountHolderOptionalFields.company ? ' (optional)' : '')
                                                                    }
                                                                    type="text"
                                                                    autoComplete="off"
                                                                    value={values.accountHolder.company}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                />
                                                                {get(errors, 'accountHolder.company') && get(touched, 'accountHolder.company') && (
                                                                    <div className="invalid-feedback">{get(errors, 'accountHolder.company')}</div>
                                                                )}
                                                            </div>
                                                            <div className="form-group col-md-6" />
                                                        </div>
                                                    )}

                                                    <div className="form-row">
                                                        {accountHolderEnableFields.firstName && (
                                                            <div className="form-group col-md-6">
                                                                <label htmlFor="firstName">First Name</label>
                                                                <input
                                                                    className={
                                                                        get(errors, 'accountHolder.firstName') && get(touched, 'accountHolder.firstName')
                                                                            ? 'form-control is-invalid'
                                                                            : 'form-control'
                                                                    }
                                                                    name="accountHolder.firstName"
                                                                    placeholder={
                                                                        'First Name' + (accountHolderOptionalFields.firstName ? ' (optional)' : '')
                                                                    }
                                                                    type="text"
                                                                    autoComplete="off"
                                                                    value={values.accountHolder.firstName}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                />
                                                                {get(errors, 'accountHolder.firstName') && get(touched, 'accountHolder.firstName') && (
                                                                    <div className="invalid-feedback">{get(errors, 'accountHolder.firstName')}</div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {accountHolderEnableFields.middleName && (
                                                            <div className="form-group col-md-6">
                                                                <label htmlFor="middleName">Middle Name</label>
                                                                <input
                                                                    className={
                                                                        get(errors, 'accountHolder.middleName') && get(touched, 'accountHolder.middleName')
                                                                            ? 'form-control is-invalid'
                                                                            : 'form-control'
                                                                    }
                                                                    name="accountHolder.middleName"
                                                                    placeholder={
                                                                        'First Name' + (accountHolderOptionalFields.middleName ? ' (optional)' : '')
                                                                    }
                                                                    type="text"
                                                                    autoComplete="off"
                                                                    value={values.accountHolder.middleName}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                />
                                                                {get(errors, 'accountHolder.middleName') && get(touched, 'accountHolder.middleName') && (
                                                                    <div className="invalid-feedback">{get(errors, 'accountHolder.middleName')}</div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {accountHolderEnableFields.lastName && (
                                                            <div className="form-group col-md-6">
                                                                <label htmlFor="lastName">Last Name</label>
                                                                <input
                                                                    className={
                                                                        get(errors, 'accountHolder.lastName') && get(touched, 'accountHolder.lastName')
                                                                            ? 'form-control is-invalid'
                                                                            : 'form-control'
                                                                    }
                                                                    name="accountHolder.lastName"
                                                                    placeholder={
                                                                        'Last Name' + (accountHolderOptionalFields.lastName ? ' (optional)' : '')
                                                                    }
                                                                    type="text"
                                                                    autoComplete="off"
                                                                    value={values.accountHolder.lastName}
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                />
                                                                {get(errors, 'accountHolder.lastName') && get(touched, 'accountHolder.lastName') && (
                                                                    <div className="invalid-feedback">{get(errors, 'accountHolder.lastName')}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="form-row">
                                                        {accountHolderEnableFields.personalEmail && (
                                                            <div className="form-group col-md-6">
                                                                <label htmlFor="personalEmail">Email address</label>
                                                                <input
                                                                    className={
                                                                        get(errors, 'accountHolder.personalEmail') &&
                                                                        get(touched, 'accountHolder.personalEmail')
                                                                            ? 'form-control is-invalid'
                                                                            : 'form-control'
                                                                    }
                                                                    name="accountHolder.personalEmail"
                                                                    placeholder={
                                                                        'Email' + (accountHolderOptionalFields.personalEmail ? ' (optional)' : '')
                                                                    }
                                                                    type="text"
                                                                    autoComplete="off"
                                                                    value={values.accountHolder.personalEmail}
                                                                    onChange={handleChange}
                                                                    onBlur={(e) => {
                                                                        handleBlur(e);
                                                                        this.checkEmail(
                                                                            status,
                                                                            setStatus,
                                                                            'accountHolder.personalEmail',
                                                                            e.target.value
                                                                        );
                                                                    }}
                                                                />
                                                                {get(errors, 'accountHolder.personalEmail') &&
                                                                    get(touched, 'accountHolder.personalEmail') && (
                                                                        <div className="invalid-feedback">{get(errors, 'accountHolder.personalEmail')}</div>
                                                                    )}
                                                                {get(status, 'accountHolder.personalEmail') && (
                                                                    <div className="invalid-feedback">{get(status, 'accountHolder.personalEmail')}</div>
                                                                )}
                                                            </div>
                                                        )}
                                                        {accountHolderEnableFields.businessEmail && (
                                                            <div className="form-group col-md-6">
                                                                <label htmlFor="businessEmail">Business Email</label>
                                                                <input
                                                                    className={
                                                                        get(errors, 'accountHolder.businessEmail') &&
                                                                        get(touched, 'accountHolder.businessEmail')
                                                                            ? 'form-control is-invalid'
                                                                            : 'form-control'
                                                                    }
                                                                    name="accountHolder.businessEmail"
                                                                    placeholder={
                                                                        'Email' + (accountHolderOptionalFields.businessEmail ? ' (optional)' : '')
                                                                    }
                                                                    type="text"
                                                                    autoComplete="off"
                                                                    value={values.accountHolder.businessEmail}
                                                                    onChange={handleChange}
                                                                    onBlur={(e) => {
                                                                        handleBlur(e);
                                                                        this.checkEmail(
                                                                            status,
                                                                            setStatus,
                                                                            'accountHolder.businessEmail',
                                                                            e.target.value
                                                                        );
                                                                    }}
                                                                />
                                                                {get(errors, 'accountHolder.businessEmail') &&
                                                                    get(touched, 'accountHolder.businessEmail') && (
                                                                        <div className="invalid-feedback">{get(errors, 'accountHolder.businessEmail')}</div>
                                                                    )}
                                                                {get(status, 'accountHolder.businessEmail') && (
                                                                    <div className="invalid-feedback">{get(status, 'accountHolder.businessEmail')}</div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {accountHolderEnableFields.mobileNumber && (
                                                            <div className="form-group col-md-6">
                                                                <label htmlFor="new-acc-phonemobile">Mobile Phone</label>

                                                                <div className="input-group">
                                                                    <input
                                                                        type="text"
                                                                        name="accountHolder.mobileNumber.number"
                                                                        value={values.accountHolder.mobileNumber.number}
                                                                        className={
                                                                            get(errors, 'accountHolder.mobileNumber.number') &&
                                                                            get(touched, 'accountHolder.mobileNumber.number')
                                                                                ? 'form-control is-invalid'
                                                                                : 'form-control'
                                                                        }
                                                                        autoComplete="new-password"
                                                                        placeholder={
                                                                            'Mobile Phone' + (accountHolderOptionalFields.mobileNumber ? ' (optional)' : '')
                                                                        }
                                                                        onChange={(evt) => {
                                                                            handleChange(evt);
                                                                            this.resetAuthCodeState(setFieldValue);
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                    />

                                                                    <div
                                                                        className={
                                                                            !showVerificationMobileNumber && !authCodeLoader && !authCodeVerifyLoader
                                                                                ? 'input-group-append input-authcode-send'
                                                                                : 'input-group-append input-authcode-send d-none'
                                                                        }
                                                                    >
                                                                        <button
                                                                            className="btn btn-outline-secondary btn-authcode-send"
                                                                            type="button"
                                                                            disabled={values.accountHolder.mobileNumber.number.length === 0}
                                                                            onClick={() => this.onSubmitAuthCode(values.accountHolder.mobileNumber.number)}
                                                                        >
                                                                            <i className="fas fa-paper-plane" /> Send
                                                                        </button>
                                                                    </div>

                                                                    {get(errors, 'accountHolder.mobileNumber.number') &&
                                                                        get(touched, 'accountHolder.mobileNumber.number') && (
                                                                            <div className="invalid-feedback">
                                                                                {get(errors, 'accountHolder.mobileNumber.number')}
                                                                            </div>
                                                                        )}
                                                                </div>

                                                                <div
                                                                    className={
                                                                        showVerificationMobileNumber &&
                                                                        !authCodeLoader &&
                                                                        !authCodeVerifyLoader &&
                                                                        isAuthCodeValid === null
                                                                            ? 'input-group-append input-authcode-validate justify-content-between mt-2'
                                                                            : 'input-group-append input-authcode-validate d-none'
                                                                    }
                                                                >
                                                                    <div className="input-group-append input-authcode-input">
                                                                        <div className="input-group">
                                                                            <div className="input-group-prepend">
                                        <span className="input-group-text">
                                          <i className="fas fa-key" />
                                        </span>
                                                                            </div>
                                                                            <input
                                                                                type="text"
                                                                                name="authCode"
                                                                                value={authCode}
                                                                                onChange={this.onChangeAuthCodeHandler}
                                                                                className="form-control w-120px authcode-key-phone"
                                                                                placeholder="AuthCode"
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        className="btn btn-outline-secondary btn-authcode-validate"
                                                                        type="button"
                                                                        onClick={() =>
                                                                            this.onSubmitVerifyAuthCode(
                                                                                values.accountHolder.mobileNumber.number,
                                                                                setFieldValue
                                                                            )
                                                                        }
                                                                    >
                                                                        <i className="fas fa-check" /> Verify
                                                                    </button>

                                                                    <button
                                                                        className="btn btn-outline-secondary btn-authcode-send"
                                                                        type="button"
                                                                        disabled={values.accountHolder.mobileNumber.number.length === 0}
                                                                        onClick={() => this.onSubmitAuthCode(values.accountHolder.mobileNumber.number)}
                                                                    >
                                                                        <i className="fas fa-paper-plane" /> Resend
                                                                    </button>
                                                                </div>

                                                                <div className="input-group-append input-authcode-status mt-2">
                                                                    {authCodeLoader && (
                                                                        <span className="input-group-text">
                                      <i className="fas fa-circle-notch fa-spin ml-1 mr-1" />
                                                                            &nbsp;Requesting
                                    </span>
                                                                    )}
                                                                    {authCodeVerifyLoader && (
                                                                        <span className="input-group-text">
                                      <i className="fas fa-circle-notch fa-spin ml-1 mr-1" />
                                                                            &nbsp;Verifying...
                                    </span>
                                                                    )}
                                                                    {isAuthCodeValid === true && !authCodeLoader && (
                                                                        <span className="input-group-text text-success">
                                      <i className="fas fa-check" />
                                                                            &nbsp;Valid
                                    </span>
                                                                    )}
                                                                    {isAuthCodeValid === false && !authCodeLoader && (
                                                                        <span className="input-group-text text-danger">
                                      <i className="fas fa-exclamation-triangle" />
                                                                            &nbsp;Invalid
                                    </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </>
                                            )}

                                            {showSuggestedAddress ? (
                                                <div>
                                                    {addressValidationLoader ? (
                                                        <AccountHolderLoader />
                                                    ) : (
                                                        <div className="form-row">
                                                            {suggestedAddress !== undefined ? (
                                                                <div className="cmv-container-subpanel cmv-container-subpanel-suggested-address">
                                                                    <h5>
                                                                        <i className="fas fa-database" /> Update Billing Address
                                                                    </h5>

                                                                    <p>The following address was found in our database.</p>

                                                                    <h6>Validated Address</h6>
                                                                    <address>
                                                                        {suggestedAddress.address1} {suggestedAddress.address2}
                                                                        <br />
                                                                        {suggestedAddress.city}, {suggestedAddress.state.abbreviation}{' '}
                                                                        {generateFullZip(suggestedAddress)}
                                                                    </address>

                                                                    <h6>Entered Address</h6>
                                                                    <address>{this.formatAddress(this.props.modalProps.validatedAddress)}</address>

                                                                    <div className="d-flex justify-content-end mt-1">
                                                                        <button
                                                                            onClick={this.closeAddressSuggestion}
                                                                            className="btn btn-outline-primary mr-1 btn-address-keep"
                                                                            type="button"
                                                                        >
                                                                            Keep Entered
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={this.useSuggestedAddress}
                                                                            className="btn btn-primary btn-address-use-suggested"
                                                                        >
                                                                            Use Validated
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="cmv-container-subpanel cmv-container-subpanel-suggested-address">
                                                                    <h5>
                                                                        <i className="fas fa-database" /> Update Billing Address
                                                                    </h5>

                                                                    <p>We could not find this address in our database.</p>

                                                                    <h6>Validated Address</h6>
                                                                    <address>Reason: {suggestedAddressError}</address>

                                                                    <h6>Entered Address</h6>
                                                                    <address>{this.formatAddress(this.props.modalProps.validatedAddress)}</address>

                                                                    <div className="d-flex justify-content-end mt-1">
                                                                        <button
                                                                            onClick={this.closeAddressSuggestion}
                                                                            className="btn btn-outline-primary mr-1 btn-address-keep"
                                                                            type="button"
                                                                        >
                                                                            Keep Entered
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                        </div>

                                        <div className="modal-footer">
                                            <button className="btn" onClick={hideModal}>
                                                Cancel
                                            </button>
                                            <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
                                                Save {isSubmitting && <i className="fas fa-spin fa-circle-notch"></i>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    />
                </div>
                <div className="modal-backdrop show" tabIndex="1" />
            </Fragment>
        );
    }
}

AccountModal.propTypes = {
    modalProps: PropTypes.object.isRequired,
    accountTypesData: PropTypes.array.isRequired,
    accountTermsData: PropTypes.array.isRequired,
    newOrderLoader: PropTypes.bool,
    authCodeLoader: PropTypes.bool,
    authCodeVerifyLoader: PropTypes.bool,
    hideModal: PropTypes.func,
    createNewOrder: PropTypes.func,
    getAccountTypes: PropTypes.func,
    getAccountTypeDetails: PropTypes.func,
    verifyAuthCode: PropTypes.func,
    sendAuthCode: PropTypes.func,
    createNewAccount: PropTypes.func,
    billingAddressValidation: PropTypes.func
};

const setNewOrderLoader = createLoadingSelector(['CREATE_ORDER']);
const setAuthCodeLoader = createLoadingSelector(['SEND_AUTH_CODE']);
const setAuthCodeVerify = createLoadingSelector(['VERIFY_AUTH_CODE']);

const mapStateToProps = (state) => {
    const modalProps = getModalData(state).modalProps,
        newOrderLoader = setNewOrderLoader(state),
        accountTypesData = getCreateServiceRequest(state).accountTypes,
        accountTermsData = getCreateServiceRequest(state).accountTerms,
        billCyclesData = getCreateServiceRequest(state).billCycles,
        authCodeLoader = setAuthCodeLoader(state),
        authCodeVerifyLoader = setAuthCodeVerify(state);

    return {
        modalProps,
        newOrderLoader,
        accountTypesData,
        accountTermsData,
        authCodeLoader,
        authCodeVerifyLoader,
        billCyclesData
    };
};

const mapDispatchToProps = {
    hideModal,
    getAccountTypes,
    getAccountTypeDetails,
    verifyAuthCode,
    sendAuthCode,
    createNewAccount,
    createNewOrder,
    billingAddressValidation
};

export default connect(mapStateToProps, mapDispatchToProps)(AccountModal);
